import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { HardHat, SealCheck, CaretDown, CaretRight, LockKey, ChalkboardTeacher, ClipboardText, FilePdf } from "@phosphor-icons/react";
import RamsReadView from "./RamsReadView";
import DraForm from "./DraForm";
import { OUTCOME_META } from "./DraLogPanel";
import { downloadBlob } from "./PdfBrandingDialog";
import { fmtDateTime } from "../../lib/dateUtil";
import TalkAudioPlayer from "./TalkAudioPlayer";
import OperativeIncidentCard from "../hse/OperativeIncidentCard";

function Block({ icon: Icon, title, badge, children, testId }) {
  return (
    <div className="border-t border-border pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2"><Icon size={16} className="text-primary" /><h3 className="text-sm font-semibold flex-1">{title}</h3>{badge}</div>
      {children}
    </div>
  );
}

/** Operative's H&S for one job: read + acknowledge issued RAMS, confirm permit precautions, acknowledge toolbox talks and
 *  complete the point-of-work dynamic risk assessment. Publishes readiness to the job page for gating. */
export default function OperativeHsPanel({ entityType, entityId, arrived, onStatusChange }) {
  const { user } = useAuth();
  const myId = user?._id;
  const [d, setD] = useState(null);
  const [lib, setLib] = useState(null);
  const [openRams, setOpenRams] = useState(null);
  const [checks, setChecks] = useState({});
  const [redoDra, setRedoDra] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");

  const load = useCallback(() => API.get(`/in-house-portal/${entityType}/${entityId}/hs`).then(({ data }) => setD(data)).catch(() => setD({ rams: [], permits: [], toolbox_talks: [], my_dra: [], dra_questions: [] })), [entityType, entityId]);
  useEffect(() => { load(); API.get("/hs/library").then(({ data }) => setLib(data)).catch(() => {}); }, [load]);

  useEffect(() => {
    if (!d || !onStatusChange) return;
    onStatusChange({ loaded: true, ramsTotal: d.rams.length, ramsAcked: d.rams.every(r => r.my_acknowledged_at), draToday: !!d.has_dra_today });
  }, [d, onStatusChange]);

  if (!d) return null;
  const hasAnything = d.rams.length || d.permits.length || d.toolbox_talks.length;
  if (!hasAnything && !arrived && d.my_dra.length === 0) return null;

  const ack = async (r) => {
    try { await API.post(`/in-house-portal/rams/${r.id}/acknowledge`); toast.success("RAMS acknowledged — stay safe"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const ackTalk = async (t) => {
    try { await API.post(`/in-house-portal/toolbox-talks/${t.id}/acknowledge`); toast.success("Toolbox talk acknowledged"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmPermit = async (p) => {
    const cur = checks[p.id] || (p.precautions || []).map(x => x.checked);
    try {
      const { data } = await API.post(`/in-house-portal/permits/${p.id}/confirm`, { checked: cur });
      toast.success(data.status === "active" ? "All precautions confirmed — permit is active" : "Precautions saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const permitPdf = async (p) => {
    setPdfBusy(p.id);
    try {
      const { data } = await API.get(`/in-house-portal/permits/${p.id}/pdf`, { responseType: "blob" });
      downloadBlob(data, `${p.reference || "permit"}.pdf`);
    } catch { toast.error("Couldn't download the permit PDF"); }
    finally { setPdfBusy(""); }
  };
  const todayDra = d.my_dra.find(x => (x.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10));

  return (
    <div className="bg-card border border-border rounded-lg p-5 border-l-4 border-l-amber-400" data-testid="operative-hs-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2"><HardHat className="h-5 w-5 text-amber-500" weight="fill" /> Health &amp; Safety</h2>
        <OperativeIncidentCard compact entityType={entityType} entityId={entityId} />
      </div>

      {d.rams.length > 0 && (
        <Block icon={SealCheck} title="RAMS" testId="ohs-rams" badge={d.needs_rams_ack ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Read &amp; acknowledge before arriving</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Acknowledged</span>}>
          {d.rams.map(r => {
            const isOpen = openRams === r.id;
            return (
              <div key={r.id} className="border border-border rounded-md mb-2" data-testid={`ohs-rams-${r.id}`}>
                <button type="button" onClick={() => setOpenRams(isOpen ? null : r.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40" data-testid={`ohs-rams-toggle-${r.id}`}>
                  {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <span className="text-xs font-semibold flex-1">{r.title} <span className="text-muted-foreground font-normal">· {r.reference} v{r.version}</span></span>
                  {r.my_acknowledged_at ? <span className="text-[10px] text-emerald-700">✓ {fmtDateTime(r.my_acknowledged_at)}</span> : <span className="text-[10px] text-amber-700 font-semibold">Not yet read</span>}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <RamsReadView rams={r} permitTypes={lib?.permit_types || []} />
                    {!r.my_acknowledged_at && <Button onClick={() => ack(r)} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700" data-testid={`ohs-rams-ack-${r.id}`}><SealCheck size={16} className="mr-1" /> I have read and understood this RAMS</Button>}
                  </div>
                )}
              </div>
            );
          })}
        </Block>
      )}

      {d.permits.length > 0 && (
        <Block icon={LockKey} title="Permits to work" testId="ohs-permits">
          {d.permits.map(p => {
            const cur = checks[p.id] || (p.precautions || []).map(x => x.checked);
            const mine = !p.issued_to?.length || p.issued_to.some(o => o.user_id === myId);
            return (
              <div key={p.id} className="border border-border rounded-md p-3 mb-2" data-testid={`ohs-permit-${p.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs font-semibold">{p.type_label} <span className="text-muted-foreground font-normal">· {p.reference}{p.location ? ` · ${p.location}` : ""}</span></p>
                  <span className="flex items-center gap-1.5">
                    <button type="button" onClick={() => permitPdf(p)} disabled={pdfBusy === p.id} className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium hover:bg-muted disabled:opacity-50" title="Download this permit as a PDF for your phone" data-testid={`ohs-permit-pdf-${p.id}`}>
                      <FilePdf size={12} /> {pdfBusy === p.id ? "…" : "PDF"}
                    </button>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{p.status}</span>
                  </span>
                </div>
                {p.valid_from && <p className="text-[11px] text-muted-foreground">Valid {fmtDateTime(p.valid_from)} → {fmtDateTime(p.valid_to)}</p>}
                {p.isolation_details && <p className="text-[11px] mt-1"><span className="text-muted-foreground">Isolations:</span> {p.isolation_details}</p>}
                <ul className="mt-2 space-y-1">
                  {(p.precautions || []).map((pr, i) => (
                    <li key={i}><label className="flex items-start gap-2 text-xs cursor-pointer">
                      <input type="checkbox" className="mt-0.5" checked={!!cur[i]} onChange={e => setChecks(c => ({ ...c, [p.id]: cur.map((v, idx) => (idx === i ? e.target.checked : v)) }))} data-testid={`ohs-permit-${p.id}-prec-${i}`} />
                      <span className={cur[i] ? "text-muted-foreground line-through" : ""}>{pr.text}</span></label></li>
                  ))}
                </ul>
                {mine && <Button size="sm" variant="outline" className="mt-2" onClick={() => confirmPermit(p)} data-testid={`ohs-permit-confirm-${p.id}`}>Confirm precautions ({cur.filter(Boolean).length}/{cur.length})</Button>}
              </div>
            );
          })}
        </Block>
      )}

      {d.toolbox_talks.length > 0 && (
        <Block icon={ChalkboardTeacher} title="Toolbox talks" testId="ohs-talks">
          {d.toolbox_talks.map(t => (
            <div key={t.id} className="border border-border rounded-md p-3 mb-2" data-testid={`ohs-talk-${t.id}`}>
              <p className="text-xs font-semibold">{t.topic} <span className="text-muted-foreground font-normal">· {t.delivered_by_name}</span></p>
              <TalkAudioPlayer talkId={t.id} audio={t.audio} onRefresh={load} testId={`ohs-talk-audio-${t.id}`} />
              <ul className="list-disc pl-4 text-xs mt-2 space-y-0.5">{(t.points || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
              {t.my_acknowledged_at ? <p className="text-[11px] text-emerald-700 mt-2">✓ Acknowledged {fmtDateTime(t.my_acknowledged_at)}</p> : <Button size="sm" variant="outline" className="mt-2" onClick={() => ackTalk(t)} data-testid={`ohs-talk-ack-${t.id}`}>I've read this talk</Button>}
            </div>
          ))}
        </Block>
      )}

      <Block icon={ClipboardText} title="Dynamic risk assessment" testId="ohs-dra" badge={todayDra ? <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${OUTCOME_META[todayDra.outcome]?.cls}`}>{OUTCOME_META[todayDra.outcome]?.label}</span> : d.rams.length ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Required before you complete</span> : null}>
        {todayDra && !redoDra ? (
          <div className="text-xs space-y-1">
            <p className="text-muted-foreground">Completed {fmtDateTime(todayDra.created_at)}{todayDra.risk_flags ? ` · ${todayDra.risk_flags} flag(s)` : ""}.</p>
            {todayDra.hazards_noted && <p>Noted: {todayDra.hazards_noted}</p>}
            {todayDra.controls && <p>Controls: {todayDra.controls}</p>}
            <Button size="sm" variant="outline" onClick={() => setRedoDra(true)} data-testid="ohs-dra-redo">Conditions changed — reassess</Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-2">{arrived ? "Take a minute before you start. Check the site against the RAMS and answer honestly." : "Do this once you're on site, before starting work."}</p>
            <DraForm entityType={entityType} entityId={entityId} questions={d.dra_questions || []} ramsId={d.rams[0]?.id} onSubmitted={() => { setRedoDra(false); load(); }} />
          </>
        )}
      </Block>
    </div>
  );
}
